import { useEffect, useRef } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Options accepted by `useGlobalShortcuts`.
 *
 * Each callback is invoked when the matching chord is pressed. The hook
 * is intentionally callback-driven so the layout component (which owns
 * the palette/assistant state) stays the source of truth.
 */
export interface GlobalShortcutsOptions {
  /** Open the command palette. */
  onOpenPalette: () => void;
  /** Toggle the AI Assistant overlay. */
  onToggleAssistant: () => void;
  /** Toggle the sidebar collapsed state. */
  onToggleSidebar: () => void;
  /** Trigger a logout flow (typically with a confirm dialog). */
  onQuit: () => void;
  /** React Router navigate. */
  navigate: NavigateFunction;
}

/**
 * True when the keyboard event originated from a text-editing surface.
 * Shortcuts are suppressed in these cases so typing isn't disrupted.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Sections of the Tabs hub, in the order they appear in the top nav.
 * `Cmd/Ctrl + Shift + [` / `]` cycle through these.
 */
const TABS_HUB_SECTIONS = [
  'live',
  'memory',
  'time-machine',
  'recycle',
  'handoff',
  'colors',
  'voice',
  'predict',
] as const;

/**
 * Move to the previous (-1) or next (+1) section in the Tabs hub,
 * based on the current `?section=` query param. If we aren't on the
 * Tabs hub, jump straight to `live` (or its neighbor).
 */
function cycleTabSection(navigate: NavigateFunction, direction: -1 | 1): void {
  const onTabsHub = window.location.pathname === '/tabs';
  const current = new URLSearchParams(window.location.search).get('section');
  const idx = current ? TABS_HUB_SECTIONS.indexOf(current as typeof TABS_HUB_SECTIONS[number]) : -1;

  let nextIdx: number;
  if (!onTabsHub || idx === -1) {
    nextIdx = direction === 1 ? 0 : TABS_HUB_SECTIONS.length - 1;
  } else {
    nextIdx = (idx + direction + TABS_HUB_SECTIONS.length) % TABS_HUB_SECTIONS.length;
  }
  const nextSection = TABS_HUB_SECTIONS[nextIdx];
  navigate(`/tabs?section=${nextSection}`);
}

/**
 * Global keyboard shortcuts for the IUILayout.
 *
 *   Cmd/Ctrl + K         → open command palette
 *   Cmd/Ctrl + Shift + A → toggle AI Assistant
 *   Cmd/Ctrl + P         → open command palette (quick switch)
 *   Cmd/Ctrl + E         → go to sessions
 *   Cmd/Ctrl + D         → duplicate (dispatches `khiimori:duplicate`)
 *   Cmd/Ctrl + G         → find next (open tabs hub)
 *   Cmd/Ctrl + ,         → open settings
 *   Cmd/Ctrl + S         → save (dispatches `khiimori:save`)
 *   Cmd/Ctrl + H         → hide / toggle sidebar
 *   Cmd/Ctrl + Q         → quit / log out
 *   Cmd/Ctrl + Shift + P → open project search
 *   Cmd/Ctrl + Shift + F → find in all
 *   Cmd/Ctrl + Shift + G → find previous (open tabs hub)
 *   Cmd/Ctrl + Shift + [ → previous tab section
 *   Cmd/Ctrl + Shift + ] → next tab section
 *   Cmd/Ctrl + /         → toggle comment
 *
 * `Ctrl` is the primary key on Linux/Windows; `Meta` (⌘) is honored on
 * macOS so the chords work everywhere. All handlers `preventDefault`
 * to override browser defaults (Ctrl+P = print, Ctrl+S = save page,
 * Ctrl+H = history, Ctrl+Q = quit browser, etc.).
 */
export function useGlobalShortcuts(options: GlobalShortcutsOptions): void {
  // Keep latest callbacks accessible without re-binding the listener on
  // every render. The listener is registered once on mount.
  const optsRef = useRef(options);
  optsRef.current = options;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      // Don't hijack typing — but always allow ⌘/Ctrl + , and ⌘/Ctrl + Q
      // because users expect those to work even from form fields.
      const allowInInputs =
        e.key === ',' || e.key.toLowerCase() === 'q';
      if (isEditableTarget(e.target) && !allowInInputs) return;

      const key = e.key.toLowerCase();

      switch (key) {
        case 'k':
          // Don't fire on Shift+Cmd+K (reserved for future use)
          if (e.shiftKey) return;
          e.preventDefault();
          optsRef.current.onOpenPalette();
          return;

        case 'a':
          if (!e.shiftKey) return;
          e.preventDefault();
          optsRef.current.onToggleAssistant();
          return;

        case 'p':
          if (!e.shiftKey) return;
          e.preventDefault();
          optsRef.current.navigate('/insights?section=overview');
          return;

        case 'e':
          e.preventDefault();
          optsRef.current.navigate('/settings?section=sessions');
          return;

        case 'd':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('khiimori:duplicate'));
          toast.info('Duplicate', {
            description: 'Duplicate shortcut sent to the active view.',
          });
          return;

        case 'g':
          e.preventDefault();
          optsRef.current.navigate(
            e.shiftKey ? '/tabs?section=time-machine' : '/tabs?section=live'
          );
          return;

        case ',':
          e.preventDefault();
          optsRef.current.navigate('/settings');
          return;

        case 's':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('khiimori:save'));
          toast.success('Saved', {
            description: 'Current view has been saved.',
          });
          return;

        case 'h':
          e.preventDefault();
          optsRef.current.onToggleSidebar();
          return;

        case 'q':
          e.preventDefault();
          optsRef.current.onQuit();
          return;

        case 'f':
          if (!e.shiftKey) return;
          e.preventDefault();
          optsRef.current.navigate('/insights');
          return;

        case '[':
          if (!e.shiftKey) return;
          e.preventDefault();
          cycleTabSection(optsRef.current.navigate, -1);
          return;

        case ']':
          if (!e.shiftKey) return;
          e.preventDefault();
          cycleTabSection(optsRef.current.navigate, +1);
          return;

        case '/':
          e.preventDefault();
          optsRef.current.navigate('/settings?section=comments');
          return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}
